import { Buffer } from 'buffer';

import { Icon } from '@nike/eds';
import {
  Loading, Container, Row
} from '@nike/frame-component-library';
import { match, Default as _ } from '@nike/rcf-fp';
import zlib from 'browserify-zlib';
import LocaleCode from 'locale-code';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  loadAdminPermissions, loadTranslations, loadBodyTranslations, toggleUi,
  setNposValue
} from '../../redux/actions';
import Auth from '../../utils/Auth';
import { NPOS_APP, TRUE_APP } from '../../utils/constants';
import { isEMEA } from '../../utils/country-mappings';
import {
  getStyles,
  getLocaleOverride,
  getHistoricalDataErrorMessage,
  isGreaterChinaGeo, isHiveAthenaError, isProdEnv, removeMidNumber
} from '../../utils/ej-utils';
import {
  currencyFormatter, currencyFormatterException, dateFormatter, dateFormatterTable, totalIsNegative
} from '../../utils/formatting';
import { getApplicationSource } from '../../utils/iframeMessaging';
import { locales } from '../../utils/locales';
import { getTransactions } from '../../utils/service-calls';
import initializePKCEFlow from '../OktaAuth/PKCEFlow';
import {
  ButtonBlack, ButtonWhite, StoreSelect, Select, TableRowClick,
} from '../reusable';

import './index.css';

import { isNposNewUiSwitchAvailable, isNposNewUiEnabledByDefault } from './Npos/config/npos-config';
import NposHeader from './Npos/NposHeader';
import { toNposDateTimeFormat, getTotalItemsPurchased } from './Npos/nposUtils';
import PrintReceiptModal from './Npos/PrintReceiptModal';
import OtherInformation from './OtherInformation';
import ReceiptView from './ReceiptView';
import TransactionBlob from './TransactionBlob';
import TransactionInformation from './TransactionInformation';

const CARD_NUMBER = 4;
const DEFAULT_SCROLL_DOWN_PIXEL = 200;
const FAILED_TO_FETCH = 'Failed to fetch data. Please narrow search to particular month or date, and try again.';
const SOMETHING_WENT_WRONG = 'Something went wrong. Please try again.';

const transactionTypeOptions = [
  { label: 'Regular Sales & Returns', value: 'RetailTransaction' },
  { label: 'Returns', value: 'Return' },
  { label: 'No Sale', value: 'NoSale' },
  { label: 'Media Exchange', value: 'StoredValueTransaction' },
];

class ElectronicJournal extends React.Component {
  CLEAR_INPUT = {
    athleteLoginNumber: '',
    billToEmail: '',
    cardNumber: '',
    delivered: false,
    ejResponse: [],
    employeeDiscount: false,
    errorMessage: '',
    fetched: false,
    fetching: false,
    freeText: '',
    generalDiscount: false,
    maximumAmount: '',
    minimumAmount: '',
    o2oEvents: '',
    orderId: '',
    originalTransactionNumber: '',
    priceOverride: false,
    promotion: false,
    registerNumber: '',
    resume: false,
    suspend: false,
    tenderType: '',
    tillId: '',
    trainingMode: '',
    transactionNumber: '',
    transactionType: transactionTypeOptions[0].value,
    transactionUUID: '',
    upcCode: '',
    viewingEJ: true,
    void: false
  };

  // eslint-disable-next-line sort-class-members/sort-class-members
  constructor(props) {
    super(props);
    this.state = {
      ...this.CLEAR_INPUT,
      authGroups: Auth.getAuthGroups(),
      authToken: Auth.getAuth(),
      isJournalViewError: false,
      isNpos: getApplicationSource() === NPOS_APP,
      isPrintingSuccessful: false,
      isReadyForAutoFetch: false,
      language: 'English',
      selectedCountry: null,
      selectedDates: {
        end: moment.tz().hour(23).minute(59),
        start: moment.tz().clone().hour(0)
          .minute(0),
      },
      selectedStore: null,
      shouldTranslateButtonDisplay: false,
      storeLanguage: 'English',
      storeLocale: 'en-US',
      storeTimezone: 'UTC',
      today: moment.tz().hour(23).minute(59),
    };
    props.loadAdminPermissions(Auth.isAdmin());
    if (getApplicationSource() === NPOS_APP) {
      props.setNposValue({
        showNewUi: isNposNewUiEnabledByDefault(Auth.getAuthCountry()),
        switchUiAvailable: isNposNewUiSwitchAvailable(Auth.getAuthCountry())
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      storeLocale, selectedCountry, storeLanguage, selectedStore
    } = this.state;

    if (storeLocale !== prevState.storeLocale || selectedCountry?.value !== prevState.selectedCountry?.value || storeLanguage !== prevState.storeLanguage || selectedStore?.value !== prevState.selectedStore?.value) {
      const { localeName } = locales(this.state.storeLocale.replace(/_/g, '-'));
      const shouldButtonDisplay = selectedCountry?.value && selectedStore?.value && storeLanguage !== 'English' && localeName !== 'en-US';
      if (!shouldButtonDisplay && !this.state.isNpos) {
        this.props.loadBodyTranslations('en-US');
      }

      this.setState({ shouldTranslateButtonDisplay: shouldButtonDisplay });
    }
     if (
    this.state.errorMessage
    && prevProps.RL_ErrorMsgForOlderDataEJ !== this.props.RL_ErrorMsgForOlderDataEJ
  ) {
    this.setState({ errorMessage: getHistoricalDataErrorMessage(this.props.RL_ErrorMsgForOlderDataEJ, selectedCountry?.value) });
  }
  }

  formatAmount = (row) => (
    ['th-TH', 'en-SG'].includes(this.state?.storeLocale)
      ? totalIsNegative(currencyFormatterException(this.state.storeLocale.replaceAll('_', '-'), row.transactionAmount), row.transactionAmount, this.state.storeLocale)
      : totalIsNegative(currencyFormatter(this.state?.storeLocale.replaceAll('_', '-'), this.state?.storeCurrency, row.transactionAmount), row.transactionAmount, this.state.storeLocale) || 'N/A'
  );

  formatDate = (row) => dateFormatterTable(
    row.transactionStartDate,
    this.state.storeTimezone,
    (
      locales(this.state.storeLocale).momentFormat
        ? locales(this.state.storeLocale).momentFormat
        : 'MM/DD/YYYY'
    ),
  );

  formatDateNpos = (row) => {
    const dateTime = this.formatDate(row);
    const dateFormat = locales(this.state.storeLocale).momentFormat;
    const { date, time } = toNposDateTimeFormat(dateTime, this.state.storeTimezone, moment, dateFormat, { RL_Today: this.props.RL_Today, RL_Yesterday: this.props.RL_Yesterday });
    return (
      <article className="d-sm-flx flx-dir-sm-c">
        <p className="npos-table-cell pb2-sm">{date}</p>
        <p className="npos-table-cell-time">{time}</p>
      </article>
    );
  }

  formatDeviceNumber = (row) => {
    if (row.transaction.Transaction?.Workstation?.TypeCode && ['NikePOSInCloud', 'Mobile'].includes(row.transaction.Transaction.Workstation.TypeCode)) {
      return 'Mobile';
    }
    return row.deviceNumber;
  }

  formatItemsCount = (row) => <p style={{ whiteSpace: 'nowrap' }}>{`${row.items}`}</p>;

  formatOperator = (row) => (
    <div className="d-sm-flx flx-ai-sm-c">
      <Icon className="operator-icon" name="ProfileFilled" size="m" />
      <p className="pl2-sm">{row.operatorNumber}</p>
    </div>
  );

  getStoreInfo = () => ({
      selectedCountry: this.state.selectedCountry,
      selectedStore: this.state.selectedStore,
      storeHeaderInfo: this.state.storeHeaderInfo,
      storeLocale: this.state.storeLocale,
      storeTimezone: this.state.storeTimezone,
  });

  getWidth = () => {
    if (this.state.viewingIndex === undefined) return '10';
    return this.props.showNewUi ? '7' : '6';
  };

  handleChange = (key, value) => {
    this.props.setNposValue({ fetchingCompleted: false });
    const fetched = this.props.showNewUi ? this.state.fetched : false;

    return match(key)(
      ['selectedDates', () => this.setState((prevState) => ({
        errorMessage: '',
        fetched,
        [key]: {
          end: moment.utc(value.end).tz(prevState.storeTimezone),
          start: moment.utc(value.start).tz(prevState.storeTimezone),
        },
      }))],
      ['transactionType', () => this.setState({
        cardNumber: '',
        errorMessage: '',
        fetched,
        [key]: value,
        tenderType: '',
        viewingEJ: ['NoSale', 'RetailTransaction', 'StoredValueTransaction', 'Return'].includes(value),
      })],
      ['tenderType', () => this.setState({
        cardNumber: '',
        errorMessage: '',
        fetched,
        [key]: value,
        subTenderType: '',
      })],
      [_, () => this.setState({ errorMessage: '', fetched, [key]: value })],
    );
  };

  handleLookupError = ({ message, statusCode }) => match({ message, statusCode })(
    [isHiveAthenaError || statusCode === 504, FAILED_TO_FETCH],
    [_, SOMETHING_WENT_WRONG],
  );

  isDisabled = () => (!this.state.selectedCountry?.value
    || !this.state.selectedStore?.value
    || (this.state.cardNumber.length >= 1 && this.state.cardNumber.length !== CARD_NUMBER)
    || (!!this.state.minimumAmount && !!this.state.maximumAmount && (Number(this.state.minimumAmount) >= Number(this.state.maximumAmount)))
    || (this.state.minimumAmount ? !this.state.maximumAmount : !!this.state.maximumAmount));

  onClear = () => this.setState((prevState) => ({
    ...this.CLEAR_INPUT,
    selectedDates: {
      end: moment.tz(undefined, prevState.storeTimezone).hour(23).minute(59),
      start: moment.tz(undefined, prevState.storeTimezone).clone().hour(0)
        .minute(0),
    },
    today: moment.tz(undefined, prevState.storeTimezone).hour(23).minute(59),
  }));

  onSelect = (rowInfo, rowIndex) => {
    // * Remove the Mid number for China.
    const refinedTransaction = this.state.selectedCountry.value === 'CHN' ? removeMidNumber(rowInfo.transaction, this.state.selectedCountry.value) : rowInfo.transaction;
    return this.setState(() => ({
      transactionBlob: refinedTransaction,
      viewingIndex: rowIndex,
    }));
  };

  onSubmit = () => this.setState({
    errorMessage: '', fetching: true, viewingIndex: undefined,
  },
  () => {
    if (!Auth.isAuthenticated()) {
      const source = sessionStorage.getItem('source');
      if ([TRUE_APP, NPOS_APP].includes(source)) {
        this.props.changeLoading(true);
      } else {
        initializePKCEFlow();
      }
      return null;
    }
    const monthsAgo = this.state.today.clone().diff(this.state.selectedDates.start, 'months');
    const countryCode = this.state.selectedCountry?.value;
    const isHistorical = isEMEA(countryCode) ? monthsAgo > 24 : monthsAgo > 13;
    if (isHistorical && !Auth.hasHistoricalDateAccess()) {
      return this.setState({ errorMessage: getHistoricalDataErrorMessage(this.props.RL_ErrorMsgForOlderDataEJ, countryCode), fetching: false });
    } else {
      // formatting to match the query contract
      const ejFields = {
        cardNumber: this.state.cardNumber,
        filters: {
          company: this.state.selectedStore?.full.company.toUpperCase(),
          countryCode: this.state.selectedCountry.value,
          storeId: this.state.storeId
        },
        freeText: this.state.freeText,
        maximumAmount: this.state.maximumAmount,
        minimumAmount: this.state.minimumAmount,
        modifiers: {
          employeeDiscount: this.state.employeeDiscount,
          generalDiscount: this.state.generalDiscount,
          priceOverride: this.state.priceOverride,
          promotion: this.state.promotion,
        },
        searchFilters: {
          billToEmail: this.state.billToEmail,
          deviceNumber: this.state.registerNumber,
          operatorNumber: this.state.athleteLoginNumber,
          orderId: this.state.orderId,
          orderType: this.state.o2oEvents,
          originalTransactionNumber: this.state.originalTransactionNumber,
          tillId: this.state.tillId,
          transactionId: this.state.transactionUUID,
          transactionNumber: this.state.transactionNumber,
          upcCode: this.state.upcCode,
        },
        status: {
          canceled: this.state.void,
          delivered: this.state.delivered,
          resumed: this.state.resume,
          suspended: this.state.suspend,
        },
        subTenderType: this.state.subTenderType,
        tenderTypes: this.state.tenderType,
        trainingMode: this.state.trainingMode,
        transactionCategory: this.state.transactionType === 'Return' ? '' : this.state.transactionType,
        transactionEndDate: dateFormatter(this.state.selectedDates.end),
        transactionHasReturn: this.state.transactionType === 'Return' ? this.state.transactionType : '',
        transactionStartDate: dateFormatter(this.state.selectedDates.start),
      };
      return getTransactions(this.state.selectedCountry?.value, ejFields, this.state.authToken)
        .then((res) => ((this.setState((prevState) => ({
          ejResponse: res.body.objects.map((e, idx) => {
            const blobToJSON = JSON.parse(
              zlib.unzipSync(Buffer.from(e.transactionBlob, 'base64')).toString()
              );
            return {
              ...e,
              items: getTotalItemsPurchased({ transaction: blobToJSON }),
              rowId: idx + 1,
              transaction: blobToJSON,
            };
          }),
          selectedDates: {
            end: moment.utc(prevState.selectedDates.end).tz(prevState.storeTimezone),
            start: moment.utc(prevState.selectedDates.start).tz(prevState.storeTimezone),
          },
        })))))
        .catch((error) => this.setState({ errorMessage: this.handleLookupError(error) }))
        .finally(() => this.setState({ fetched: true, fetching: false }, () => {
          if (!this.props.showNewUi) this.scrollWindowDown();
          this.props.setNposValue({ fetchingCompleted: true, isStoreSwitched: false });
        }))
        .catch((error) => this.setState({ errorMessage: error.message }));
    }
  });

  onTranslate = () => () => {
    const { localeName } = locales(this.state.storeLocale.replaceAll('_', '-'));
    const translatedLocaleName = this.state.language === 'English' ? 'en-US' : getLocaleOverride(localeName);
    this.translateBody(translatedLocaleName);
  }

  onUiToggle = () => {
    if (isProdEnv()) return false;
    const { showNewUi } = this.props;
    return this.props.toggleUi(!showNewUi);
  }

  scrollWindowDown = (pixel = DEFAULT_SCROLL_DOWN_PIXEL) => {
    window.scrollBy({
      behavior: 'smooth',
      top: pixel,
    });
  };

  selectCountry = (selectedCountry) => {
    this.setState({
    ejResponse: [], fetched: false, selectedCountry, selectedStore: null,
  });
};

  selectStore = async ({ full, label, value }) => {
    const {
      address,
      currencies,
      id,
      locale,
      name,
      phone,
      timezone,
      localizations,
      storeConcept
    } = full;
    const localeName = locale.replaceAll('_', '-');

    if (this.state.selectedStore?.value === value) return true;

    return this.setState((prevState) => ({
      ejResponse: [],
      fetched: false,
      isReadyForAutoFetch: true,
      language: LocaleCode.getLanguageName(localeName),
      selectedDates: {
        end: moment.tz(timezone).hour(23)
          .minute(59),
        start: moment.tz(timezone).hour(0)
          .minute(0),
      },
      selectedStore: { full, label, value },
      storeCurrency: currencies[0],
      storeHeaderInfo: {
        address, localizations, name, phoneNumber: phone, storeConcept
      },
      storeId: id,
      storeLanguage: LocaleCode.getLanguageName(localeName),
      storeLocale: locale,
      storeTimezone: timezone,
      subTenderType: isGreaterChinaGeo(full.address.country) ? prevState.subTenderType : '',
      today: moment.tz(timezone).hour(23)
        .minute(59),
    }), this.translateByLocale);
  }

  setIsPrintingSuccessful = (value) => { this.setState({ isPrintingSuccessful: value }); };

  translateBody = (translatedLocaleName) => {
    this.props.loadBodyTranslations(translatedLocaleName);
    this.setState((prevState) => ({
      language: prevState.language === prevState.storeLanguage ? 'English' : prevState.storeLanguage
    }));
  };

  translateByLocale = () => {
    const localeInfo = locales(this.state.storeLocale);
    const localeName = getLocaleOverride(localeInfo.localeName);
    const locale = {
      ...localeInfo,
      localeName,
      storeCurrency: this.state.storeCurrency,
      storeLocale: this.state.storeLocale.replaceAll('_', '-')
    };
    this.props.loadTranslations(locale);
    // For NPOS EJ, body language will be inherited from NPOS App.
    if (!this.state.isNpos) {
      this.translateBody(localeName);
    }
  }

  updateErrorFlag = (isError) => {
    this.setState((prevState) => ({ ...prevState, isJournalViewError: isError }));
  };

  render = () => (
    <main className="main ta-sm-c">
      <div id="modal-root" />
      <section className="filters hide-print" style={getStyles(this.props.showNewUi, 'filters')}>
        <Container>
          <Row className={`ncss-col-sm-11 ncss-col-lg-${this.props.showNewUi ? '11' : '10'}`}>
            {!this.state.isNpos && !this.state.fetching && this.state.shouldTranslateButtonDisplay && (
              <ButtonBlack
                className="rightAlign mt3-sm ml2-sm"
                isLoading={this.state.fetching}
                label={`Translate to ${this.state.language}`}
                onClick={this.onTranslate()}
              />
            )}
            {this.state.isNpos && !this.props.showNewUi && this.props.switchUiAvailable && (
              <ButtonWhite
                className={`rightAlign mt3-sm ${this.state.fetching ? 'hidden' : ''}`}
                label="Switch to New UI"
                onClick={this.onUiToggle}
              />
            )}
          </Row>
          <Row className="ncss-col-sm-12 ncss-col-lg-12">
            {this.state.isNpos && this.props.showNewUi && (
              <NposHeader
                fields={this.state}
                handleChange={this.handleChange}
                isLoading={this.state.fetching}
                isReadyForAutoFetch={this.state.isReadyForAutoFetch}
                selectCountry={this.selectCountry}
                selectStore={this.selectStore}
                storeLocale={this.state.storeLocale}
                timezone={this.state.storeTimezone}
                onClear={this.onClear}
                onSubmit={this.onSubmit}
                onUiToggle={this.onUiToggle}
              />
            )}
          </Row>
        </Container>

        {!this.props.showNewUi && (
          <>
            <i className="g72-swoosh mt2-sm text-color-primary-dark" style={{ fontSize: 40 }} />
            <h1 className="responsive-display-2-1 text-color-primary-dark mb6-sm ta-sm-c" style={{ display: 'block' }}>{this.props.RL_ElectronicJournal}</h1>
          </>
        )}

        {!this.props.showNewUi && (
          <section className="ncss-col-sm-11 ncss-col-lg-10 border mb2-sm">
            <header className="ncss-row">
              <p className="headline-2 text-color-primary-light bg-black ta-sm-l pl2-sm">{this.props.RL_General}</p>
            </header>
            <article className="ncss-col-sm-12 ncss-col-md-11 ncss-col-lg-10 ncss-col-xl-8 p4-sm">
              <StoreSelect
                isRequired
                selectCountry={this.selectCountry}
                selectStore={this.selectStore}
                selectedCountry={this.state.selectedCountry}
                selectedStore={this.state.selectedStore}
                storeviewsFields={['id', 'address.country', 'storeNumber']}
              />

              <Select
                className="ta-sm-l va-sm-t"
                label={this.props.RL_TransactionType}
                options={transactionTypeOptions}
                value={this.state.transactionType}
                onChange={(value) => this.handleChange('transactionType', value)}
              />
            </article>
          </section>
        )}

        {!this.props.showNewUi
          && (
            <>
              <TransactionInformation
                fields={this.state}
                handleChange={this.handleChange}
                showNewUi={this.props.showNewUi}
                storeLocale={this.state.storeLocale}
                today={this.state.today}
              />
              <OtherInformation
                fields={this.state}
                handleChange={this.handleChange}
              />
              <br />
            </>
          )}

        {!this.props.showNewUi && (
          <div className="pb4-sm">
            {!this.state.fetching
            ? (
              <>
                <ButtonBlack
                  errorMessage={this.state.errorMessage}
                  id="search-button"
                  isDisabled={this.isDisabled()}
                  isLoading={this.state.fetching}
                  label={this.props.RL_Search}
                  onClick={this.onSubmit}
                />
                <ButtonWhite
                  className="mt10-sm ml5-sm"
                  label={this.props.RL_ResetSearch}
                  onClick={this.onClear}
                />
              </>
            )
            : (<article className="ncss-row"><Loading /></article>)}
          </div>
        )}
      </section>
      <section className={`ncss-col-sm-12 ncss-col-lg-12 ${this.state.ejResponse.length ? 'mt1-sm ml1-sm mr1-sm' : ''} results`} style={getStyles(this.props.showNewUi, 'results')}>
        <section className={`ncss-col-sm-11 ncss-col-lg-${this.getWidth()} ${this.props.showNewUi ? '' : 'ml10-sm mr10-sm'} va-sm-t hide-print`}>
          <TableRowClick
            showPagination
            activeRow={this.state.viewingIndex}
            columns={!this.props.showNewUi
              ? [
                  { accessor: this.formatDate, Header: this.props.RL_Date, id: 'transactionStartDate' },
                  { accessor: 'transactionNumber', Header: this.props.RL_Tran },
                  { accessor: 'deviceNumber', Header: this.props.RL_Reg },
                  { accessor: 'operatorNumber', Header: this.props.Op },
                  {
                    accessor: this.formatAmount,
                    Cell: (row) => <article className="ta-sm-r pr3-sm">{row.value}</article>,
                    Header: () => <article className="ta-sm-r pr3-sm">{this.props.RL_Amount}</article>,
                    id: 'transactionAmount',
                  },
                ]
              : [
                  {
                    accessor: this.formatDateNpos, formatType: 'string', Header: this.props.RL_Date, id: 'transactionStartDate', width: 120
                  },
                  {
                    accessor: this.formatDeviceNumber, formatType: 'number', Header: this.props.RL_Reg, id: 'deviceNumber', width: 100
                  },
                  {
                    accessor: this.formatOperator, formatType: 'number', Header: this.props.Op, id: 'operatorNumber', width: 100
                  },
                  {
                    accessor: this.formatItemsCount, formatType: 'string', Header: this.props.RL_Items, id: 'items', width: 100
                  },
                  {
                    accessor: this.formatAmount, formatType: 'number', Header: this.props.RL_Total, id: 'transactionAmount', width: 100
                  }
                ]}
            data={this.state.ejResponse}
            defaultPageSize={20}
            errorMessage={this.state.errorMessage}
            fetched={this.state.fetched && !this.state.errorMessage}
            fetching={this.state.fetching}
            handleClick={this.onSelect}
            isNpos={this.state.isNpos}
            isTransactionSelected={this.state.isTransactionSelected}
            showNewUi={this.props.showNewUi}
            storeInfo={this.getStoreInfo()}
            title={this.props.RL_MatchingTransactions}
            viewingIndex={this.state.viewingIndex}
          />
        </section>
        {this.state.fetched && this.state.viewingIndex !== undefined && (
          <section className={`ncss-col-sm-11 ncss-col-lg-5 ${this.state.authGroups.has('App.ElectronicJournal.GLOBAL') ? '' : '' } va-sm-t ${this.props.showNewUi ? 'pt5-sm' : ''}`} style={this.props.showNewUi ? { backgroundColor: '#FFF', position: 'sticky', top: 0 } : { backgroundColor: '#FFF' }}>
            {['NoSale', 'RetailTransaction', 'StoredValueTransaction', 'Return'].includes(this.state.transactionType) && this.state.authGroups.has('App.ElectronicJournal.ADMIN') && (
              <ButtonBlack
                className="mt2-sm va-sm-t mt10-sm hide-print"
                label={this.props.RL_SwitchView}
                onClick={() => this.setState((prevState) => ({ viewingEJ: !prevState.viewingEJ }))}
              />
            )}
            {this.state.viewingEJ && (
              <ReceiptView
                authToken={this.state.authToken}
                isJournalViewError={this.state.isJournalViewError}
                isNpos={this.state.isNpos}
                showNewUi={this.props.showNewUi}
                storeInfo={this.getStoreInfo()}
                transactionBlob={this.state.transactionBlob}
                transactionType={this.state.transactionType}
                updateErrorFlag={this.updateErrorFlag}
              />
            )}
            {this.state.isNpos && (
              <>
                {this.props.isPrinterDialogOpen && !this.state.isPrintingSuccessful && (
                  <PrintReceiptModal
                    authToken={this.state.authToken}
                    isPrinterDialogOpen={this.props.isPrinterDialogOpen}
                    isPrintingSuccessful={this.state.isPrintingSuccessful}
                    setIsPrintingSuccessful={this.setIsPrintingSuccessful}
                    storeInfo={this.getStoreInfo()}
                    transactionBlob={this.state.transactionBlob}
                  />
                )}
              </>
            )}
            {!this.state.viewingEJ && (
              <>
                <p className={`headline-2 text-color-primary-dark mt${(this.state.transactionType === 'StoredValueTransaction') ? '10' : '4'}-sm ta-sm-c hide-print`}>{this.props.RL_TransactionRepresentation}</p>
                <p>Transaction UUID : {this.state?.transactionBlob?.Transaction?.TransactionID}</p>
                <article className="border ta-sm-l hide-print" style={{ maxHeight: '977px', overflow: 'scroll' }}>
                  <TransactionBlob footers={this.state.transactionBlob.footer} transaction={this.state.transactionBlob.Transaction} />
                </article>
              </>
            )}
          </section>
        )}
      </section>
    </main>
  );
}

ElectronicJournal.propTypes = {
  changeLoading: PropTypes.func.isRequired,
  isPrinterDialogOpen: PropTypes.bool.isRequired,
  loadAdminPermissions: PropTypes.func.isRequired,
  loadBodyTranslations: PropTypes.func.isRequired,
  loadTranslations: PropTypes.func.isRequired,
  Op: PropTypes.string.isRequired,
  RL_Amount: PropTypes.string.isRequired,
  RL_Date: PropTypes.string.isRequired,
  RL_ElectronicJournal: PropTypes.string.isRequired,
  RL_ErrorMsgForOlderDataEJ: PropTypes.string.isRequired,
  RL_General: PropTypes.string.isRequired,
  RL_Items: PropTypes.string.isRequired,
  RL_MatchingTransactions: PropTypes.string.isRequired,
  RL_Reg: PropTypes.string.isRequired,
  RL_ResetSearch: PropTypes.string.isRequired,
  RL_Search: PropTypes.string.isRequired,
  RL_SwitchView: PropTypes.string.isRequired,
  RL_Today: PropTypes.string.isRequired,
  RL_Total: PropTypes.string.isRequired,
  RL_Tran: PropTypes.string.isRequired,
  RL_TransactionRepresentation: PropTypes.string.isRequired,
  RL_TransactionType: PropTypes.string.isRequired,
  RL_Yesterday: PropTypes.string.isRequired,
  setNposValue: PropTypes.func.isRequired,
  showNewUi: PropTypes.bool.isRequired,
  switchUiAvailable: PropTypes.bool.isRequired,
  toggleUi: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  loadAdminPermissions: bindActionCreators(loadAdminPermissions, dispatch),
  loadBodyTranslations: bindActionCreators(loadBodyTranslations, dispatch),
  loadTranslations: bindActionCreators(loadTranslations, dispatch),
  setNposValue: bindActionCreators(setNposValue, dispatch),
  toggleUi: bindActionCreators(toggleUi, dispatch),
});
const mapStateToProps = (state) => {
  const { localizedBody, nposValues } = state;
  return {
    isPrinterDialogOpen: nposValues.isPrinterDialogOpen,
    Op: localizedBody.Op,
    RL_Amount: localizedBody.RL_Amount,
    RL_Date: localizedBody.RL_Date,
    RL_ElectronicJournal: localizedBody.RL_ElectronicJournal,
    RL_ErrorMsgForOlderDataEJ: localizedBody.RL_ErrorMsgForOlderDataEJ,
    RL_General: localizedBody.RL_General,
    RL_Items: localizedBody.RL_Items,
    RL_Journal: localizedBody.RL_Journal,
    RL_MatchingTransactions: localizedBody.RL_MatchingTransactions,
    RL_PrintEJ: localizedBody.RL_PrintEJ,
    RL_Reg: localizedBody.RL_Reg,
    RL_ResetSearch: localizedBody.RL_ResetSearch,
    RL_Search: localizedBody.RL_Search,
    RL_SwitchView: localizedBody.RL_SwitchView,
    RL_Today: localizedBody.RL_Today,
    RL_Total: localizedBody.RL_Total,
    RL_Tran: localizedBody.RL_Tran,
    RL_TransactionRepresentation: localizedBody.RL_TransactionRepresentation,
    RL_TransactionType: localizedBody.RL_TransactionType,
    RL_Yesterday: localizedBody.RL_Yesterday,
    showNewUi: nposValues.showNewUi,
    switchUiAvailable: nposValues.switchUiAvailable,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ElectronicJournal);
